import { useState } from 'react';
import { adminApi } from '@/api/endpoints';
import type { Team } from '@/api/types';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

/**
 * Kürzelpflege für die Mannschaften der 3. Liga (Contract §12.2). Das Kürzel
 * wird als Anzeigename in Tippübersicht/Bonus-Tabellen verwendet, wenn der
 * volle Vereinsname zu lang ist (Fallback: voller Name).
 */
export function AdminTeamsPage() {
  const { data: teams, loading, error, reload } = useAsync<Team[]>(
    (signal) => adminApi.listLeagueTeams(signal),
    []
  );

  if (loading) return <Spinner label="Mannschaften werden geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  const sorted = [...(teams ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <Card>
        <h2>Mannschaften — 3. Liga</h2>
        <p className="muted">
          Kürzel je Mannschaft für die Anzeige in Tippübersicht/Bonus-Tabellen. Ohne Kürzel wird
          der volle Vereinsname angezeigt.
        </p>
      </Card>

      {sorted.length === 0 && (
        <Card>
          <p className="muted">
            Noch keine Mannschaften vorhanden. Stoße zunächst einen Sync an (Admin → Sync).
          </p>
        </Card>
      )}

      {sorted.map((team) => (
        <TeamCard key={team.id} team={team} onChanged={reload} />
      ))}
    </>
  );
}

function TeamCard({ team, onChanged }: { team: Team; onChanged: () => void }) {
  const [shortName, setShortName] = useState(team.short_name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await adminApi.patchTeam(team.id, shortName.trim());
      setSaved(true);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className={styles.inlineForm}>
        <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 220px' }}>
          <span className={styles.label}>Mannschaft</span>
          <input className={styles.input} value={team.name} disabled />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 1 120px' }}>
          <span className={styles.label}>Kürzel</span>
          <input
            className={styles.input}
            value={shortName}
            placeholder="z. B. SCP"
            maxLength={10}
            onChange={(e) => setShortName(e.target.value)}
          />
        </label>
        <div className={styles.actions}>
          <Button
            className={styles.btnSm}
            disabled={busy || shortName.trim() === ''}
            onClick={() => void save()}
          >
            {busy ? '…' : 'Speichern'}
          </Button>
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>Gespeichert.</div>}
    </Card>
  );
}
